import { EmptyState } from '@astryxdesign/core/EmptyState';
import { ArrowUpRightIcon } from '@phosphor-icons/react';
import { formatDuration, formatPrice } from '@/lib/format';
import type { PublicService } from '@/lib/api/schemas';
import { useStudio } from '@/features/studio/studio-data';
import { serviceIcon } from '@/components/serviceIcon';

export function groupServices(services: PublicService[]) {
  const groups = new Map<string, PublicService[]>();
  for (const service of services) {
    const key = service.category || 'Услуги';
    groups.set(key, [...(groups.get(key) ?? []), service]);
  }
  return [...groups.entries()];
}

export function serviceSummary(service: PublicService, currency: string) {
  return `${formatPrice(service.priceMinor, currency, service.priceKind)} · ${serviceLength(service)}`;
}

export function serviceLength(service: PublicService) {
  return service.occupancy === 'continuous'
    ? `машина у нас ${formatDuration(service.durationMinutes)}`
    : formatDuration(service.durationMinutes);
}

/**
 * One service as a single tappable card: name and duration on the left, price and the
 * action on the right, an optional description underneath — all inside one button, so
 * hover and press cover the whole card.
 */
export function ServiceRow({
  service,
  currency,
  selected,
  action = 'Выбрать',
  description,
  onSelect,
}: {
  service: PublicService;
  currency: string;
  selected?: boolean;
  action?: string;
  description?: string;
  onSelect: () => void;
}) {
  const Marker = serviceIcon(service);
  return (
    <button type="button" className="ui-list__item ui-list__item--stack" aria-current={selected || undefined} onClick={onSelect}>
      <span className="ui-row" style={{ width: '100%', ['--ui-gap' as string]: '14px' }}>
        <span className="ui-icon-tile">
          <Marker size={22} aria-hidden />
        </span>
        <span className="ui-stack ui-grow" style={{ ['--ui-gap' as string]: '3px' }}>
          <span className="ui-title">{service.name}</span>
          <span className="ui-caption">{serviceLength(service)}</span>
        </span>
        <span className="ui-list__end">
          <span className="ui-price">{formatPrice(service.priceMinor, currency, service.priceKind)}</span>
          <span className="ui-caption ui-list__action">{action}</span>
        </span>
        <ArrowUpRightIcon size={18} weight="bold" className="ui-arrow" aria-hidden />
      </span>
      {description ? <span className="ui-caption ui-list__desc">{description}</span> : null}
    </button>
  );
}

/**
 * A featured service as a standalone card on the home page: marker tile, name with
 * category and length, price on the right. The whole card is one button.
 */
export function ServiceCard({ service, currency, onSelect }: { service: PublicService; currency: string; onSelect: () => void }) {
  const Marker = serviceIcon(service);
  const meta = [service.category, serviceLength(service)].filter(Boolean).join(' · ');
  return (
    <button type="button" className="ui-svc" onClick={onSelect}>
      <span className="ui-svc__thumb">
        <Marker size={26} aria-hidden />
      </span>
      <span className="ui-svc__body">
        <span className="ui-svc__name">{service.name}</span>
        <span className="ui-caption">{meta}</span>
      </span>
      <span className="ui-svc__price">
        {service.priceKind === 'from' ? <span className="ui-caption">от</span> : null}
        <span>{formatPrice(service.priceMinor, currency, 'fixed')}</span>
      </span>
    </button>
  );
}

export function ServiceStep({ onSelect, selectedId }: { onSelect: (id: string) => void; selectedId: string | null }) {
  const studio = useStudio();
  if (studio.services.length === 0) {
    return <EmptyState title="Услуги скоро появятся" description="Позвоните в студию, чтобы записаться." isCompact />;
  }
  const groups = groupServices(studio.services);
  return (
    <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
      {groups.map(([category, services]) => (
        <section key={category} className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-2)' }} aria-label={category}>
          {groups.length > 1 ? <span className="ui-caption">{category}</span> : null}
          <div className="ui-list">
            {services.map((service) => (
              <ServiceRow
                key={service.id}
                service={service}
                currency={studio.currency}
                selected={service.id === selectedId}
                onSelect={() => onSelect(service.id)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
